import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * TranslatorStatusPage — P1-05.
 *
 * Live view of the per-app device-translator broker publisher state.
 * Backed by:
 *   GET /v1/admin/apps                         — app picker
 *   GET /v1/admin/apps/:appId/translator       — publisher + profiles
 *
 * Behavior:
 *   - Two-pane: app picker (left) + status detail (right).
 *   - Auto-refresh every 5s while mounted.
 *   - Detects publisher.state transitions across refreshes and surfaces a
 *     dismissable inline "State changed: A -> B" notice for live ops.
 *   - 404 on the translator endpoint is treated as an empty-info case, not
 *     a hard error.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNexusFetch } from '../api.js';
import { NexusAdminApiError } from '../api.js';
const REFRESH_INTERVAL_MS = 5000;
function stateBadgeColor(state) {
    if (state === 'connected')
        return 'success';
    if (state === 'connecting' || state === 'unstarted')
        return 'warning';
    if (state === 'error' || state === 'disconnected')
        return 'danger';
    return 'secondary';
}
function StateBadge({ state }) {
    const color = stateBadgeColor(state);
    return _jsx("span", { className: `badge text-bg-${color}`, children: state });
}
function stateBannerLabel(state) {
    switch (state) {
        case 'connected':
            return 'Connected';
        case 'connecting':
            return 'Connecting';
        case 'unstarted':
            return 'Not started';
        case 'disconnected':
            return 'Disconnected';
        case 'error':
            return 'Error';
        case 'unconfigured':
            return 'Unconfigured';
        default:
            return state;
    }
}
function formatRelative(fromIso, now) {
    if (!fromIso)
        return 'never';
    const then = Date.parse(fromIso);
    if (Number.isNaN(then))
        return fromIso;
    const deltaMs = now - then;
    if (deltaMs < 0)
        return 'just now';
    const sec = Math.floor(deltaMs / 1000);
    if (sec < 5)
        return 'just now';
    if (sec < 60)
        return `${sec} seconds ago`;
    const min = Math.floor(sec / 60);
    if (min < 60)
        return `${min} minute${min === 1 ? '' : 's'} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24)
        return `${hr} hour${hr === 1 ? '' : 's'} ago`;
    const days = Math.floor(hr / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
}
export function TranslatorStatusPage() {
    const fetcher = useNexusFetch();
    // App list ---------------------------------------------------------------
    const [apps, setApps] = useState(null);
    const [appsError, setAppsError] = useState(null);
    const [selectedAppId, setSelectedAppId] = useState('');
    // Status -----------------------------------------------------------------
    const [view, setView] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    // State-transition surface ------------------------------------------------
    const [transitionNotice, setTransitionNotice] = useState(null);
    const previousStateRef = useRef(null);
    // Tick driver so relative timestamps refresh between fetches.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, []);
    // Load app list (once on mount).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetcher('/v1/admin/apps');
                if (cancelled)
                    return;
                const list = data?.apps ?? [];
                setApps(list);
                if (list.length > 0) {
                    setSelectedAppId((prev) => prev || list[0].appId);
                }
            }
            catch (err) {
                if (cancelled)
                    return;
                const msg = err instanceof NexusAdminApiError
                    ? `${err.code}: ${err.message}`
                    : err instanceof Error
                        ? err.message
                        : 'Failed to load apps';
                setAppsError(msg);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [fetcher]);
    const loadStatus = useCallback(async (appId, opts) => {
        if (!appId)
            return;
        if (opts.showSpinner)
            setLoadingStatus(true);
        try {
            const data = await fetcher(`/v1/admin/apps/${encodeURIComponent(appId)}/translator`);
            const newState = data?.publisher?.state ?? null;
            const prev = previousStateRef.current;
            if (prev && newState && prev !== newState) {
                setTransitionNotice({ from: prev, to: newState, at: Date.now() });
            }
            previousStateRef.current = newState;
            setView({ status: data, notTracked: false });
            setStatusError(null);
            setLastRefreshedAt(Date.now());
        }
        catch (err) {
            if (err instanceof NexusAdminApiError && err.status === 404) {
                previousStateRef.current = null;
                setView({ status: null, notTracked: true });
                setStatusError(null);
                setLastRefreshedAt(Date.now());
            }
            else {
                const msg = err instanceof NexusAdminApiError
                    ? `${err.code}: ${err.message}`
                    : err instanceof Error
                        ? err.message
                        : 'Failed to load translator status';
                setStatusError(msg);
            }
        }
        finally {
            if (opts.showSpinner)
                setLoadingStatus(false);
        }
    }, [fetcher]);
    // Reset transition tracking when switching apps + kick the first fetch.
    useEffect(() => {
        if (!selectedAppId)
            return;
        previousStateRef.current = null;
        setTransitionNotice(null);
        setView(null);
        loadStatus(selectedAppId, { showSpinner: true });
    }, [selectedAppId, loadStatus]);
    // Auto-refresh loop (background; no spinner flicker).
    useEffect(() => {
        if (!selectedAppId)
            return;
        const id = window.setInterval(() => {
            loadStatus(selectedAppId, { showSpinner: false });
        }, REFRESH_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [selectedAppId, loadStatus]);
    const onManualRefresh = useCallback(() => {
        if (selectedAppId)
            loadStatus(selectedAppId, { showSpinner: true });
    }, [selectedAppId, loadStatus]);
    const publisher = view?.status?.publisher ?? null;
    const profiles = view?.status?.profiles ?? [];
    const bannerColor = useMemo(() => (publisher ? stateBadgeColor(publisher.state) : 'secondary'), [publisher]);
    return (_jsxs("div", { children: [_jsxs("div", { className: "d-flex align-items-center justify-content-between mb-3", children: [_jsx("h1", { className: "h3 mb-0", children: "Translator status" }), _jsx("button", { type: "button", className: "btn btn-outline-secondary btn-sm", onClick: onManualRefresh, disabled: !selectedAppId || loadingStatus, children: loadingStatus ? 'Refreshing…' : 'Refresh' })] }), _jsx("p", { className: "text-muted mb-3", children: "Live view of the per-app device translator. Pick an app on the left to see its MQTT publisher state (connected / disconnected / queued messages) and the codec profiles it can encode/decode. Auto-refreshes every 5 seconds; transitions in publisher state are surfaced inline." }), _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-md-4", children: _jsxs("div", { className: "card", children: [_jsx("div", { className: "card-header", children: "App" }), _jsx("div", { className: "card-body", children: appsError ? (_jsx("div", { className: "alert alert-danger mb-0", children: appsError })) : apps === null ? (_jsxs("div", { className: "d-flex align-items-center gap-2 text-muted", children: [_jsx("div", { className: "spinner-border spinner-border-sm", role: "status", "aria-hidden": "true" }), _jsx("span", { children: "Loading apps\u2026" })] })) : apps.length === 0 ? (_jsx("p", { className: "text-muted mb-0", children: "No apps registered." })) : (_jsxs(_Fragment, { children: [_jsx("label", { htmlFor: "translator-app-picker", className: "form-label", children: "Select app" }), _jsx("select", { id: "translator-app-picker", className: "form-select", value: selectedAppId, onChange: (e) => setSelectedAppId(e.target.value), children: apps.map((a) => (_jsx("option", { value: a.appId, children: a.appId }, a.appId))) })] })) })] }) }), _jsx("div", { className: "col-md-8", children: !selectedAppId ? (_jsx("div", { className: "card", children: _jsx("div", { className: "card-body text-muted", children: "Select an app on the left to view its translator status." }) })) : statusError ? (_jsx("div", { className: "card border-danger", children: _jsxs("div", { className: "card-body", children: [_jsx("h2", { className: "h5 text-danger", children: "Failed to load translator status" }), _jsx("p", { className: "mb-3", children: statusError }), _jsx("button", { type: "button", className: "btn btn-outline-danger btn-sm", onClick: onManualRefresh, children: "Retry" })] }) })) : view === null ? (_jsx("div", { className: "card", children: _jsxs("div", { className: "card-body d-flex align-items-center gap-2 text-muted", children: [_jsx("div", { className: "spinner-border spinner-border-sm", role: "status", "aria-hidden": "true" }), _jsx("span", { children: "Loading translator status\u2026" })] }) })) : view.notTracked ? (_jsx("div", { className: "card", children: _jsxs("div", { className: "card-body", children: [_jsxs("h2", { className: "h5 mb-2", children: ["No translator entry for ", selectedAppId] }), _jsx("p", { className: "text-muted mb-0", children: "This app may have no devices yet, or the translator has not been initialised on this gateway." })] }) })) : publisher ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: `alert alert-${bannerColor} d-flex align-items-center justify-content-between`, children: [_jsxs("div", { children: [_jsx("div", { className: "text-uppercase small fw-semibold opacity-75", children: "Publisher state" }), _jsx("div", { className: "h3 mb-0", children: stateBannerLabel(publisher.state) })] }), _jsx(StateBadge, { state: publisher.state })] }), transitionNotice && (_jsxs("div", { className: "alert alert-info d-flex align-items-center justify-content-between", children: [_jsxs("div", { children: ["State changed:", ' ', _jsx(StateBadge, { state: transitionNotice.from }), ' ', _jsx("span", { "aria-hidden": "true", children: "\u2192" }), ' ', _jsx(StateBadge, { state: transitionNotice.to }), _jsxs("span", { className: "text-muted ms-2 small", children: ["(", formatRelative(new Date(transitionNotice.at).toISOString(), now), ")"] })] }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Dismiss", onClick: () => setTransitionNotice(null) })] })), _jsxs("div", { className: "row row-cols-md-4 g-3 mb-3", children: [_jsx("div", { className: "col", children: _jsx("div", { className: "card h-100", children: _jsxs("div", { className: "card-body", children: [_jsx("div", { className: "text-muted small text-uppercase", children: "Publish count" }), _jsx("div", { className: "h4 mb-0", children: publisher.publishCount.toLocaleString() })] }) }) }), _jsx("div", { className: "col", children: _jsx("div", { className: "card h-100", children: _jsxs("div", { className: "card-body", children: [_jsx("div", { className: "text-muted small text-uppercase", children: "Inbound count" }), _jsx("div", { className: "h4 mb-0", children: publisher.inboundCount.toLocaleString() })] }) }) }), _jsx("div", { className: "col", children: _jsx("div", { className: `card h-100 ${publisher.queuedCount > 0 ? 'border-danger' : ''}`, children: _jsxs("div", { className: "card-body", children: [_jsx("div", { className: "text-muted small text-uppercase", children: "Queued" }), _jsx("div", { className: `h4 mb-0 ${publisher.queuedCount > 0 ? 'text-danger' : ''}`, children: publisher.queuedCount.toLocaleString() })] }) }) }), _jsx("div", { className: "col", children: _jsx("div", { className: "card h-100", children: _jsxs("div", { className: "card-body", children: [_jsx("div", { className: "text-muted small text-uppercase", children: "Last connected" }), _jsx("div", { className: "h6 mb-0", title: publisher.lastConnectedAt ?? 'never', children: formatRelative(publisher.lastConnectedAt, now) })] }) }) })] }), publisher.lastError && (_jsxs("div", { className: "alert alert-danger", children: [_jsx("h2", { className: "h6 mb-2", children: "Last error" }), _jsx("pre", { className: "mb-2 small", style: { whiteSpace: 'pre-wrap' }, children: publisher.lastError }), _jsx("div", { className: "small text-muted", children: "Stored from the last error; may not reflect current state. Refresh to update." })] })), _jsxs("div", { className: "card mb-3", children: [_jsx("div", { className: "card-header", children: "Codec profiles" }), profiles.length === 0 ? (_jsx("div", { className: "card-body text-muted", children: "No codec profiles registered for this app." })) : (_jsx("ul", { className: "list-group list-group-flush", children: profiles.map((p) => (_jsxs("li", { className: "list-group-item", children: [_jsx("div", { className: "fw-semibold", children: p.deviceType }), _jsx("div", { className: "text-muted small", children: p.description })] }, p.deviceType))) }))] }), _jsxs("div", { className: "text-muted small", children: ["Last refreshed:", ' ', lastRefreshedAt
                                            ? formatRelative(new Date(lastRefreshedAt).toISOString(), now)
                                            : 'never', ' · ', "auto-refresh every ", Math.round(REFRESH_INTERVAL_MS / 1000), "s"] })] })) : null })] })] }));
}
//# sourceMappingURL=TranslatorStatusPage.js.map